import React, { useState } from 'react';
import { OverlayContainer } from 'components/OverlayContainer';
import { TextArea } from 'components/TextArea';
import { Input } from 'components/Input';
import { FormTitle } from '@src/components/Form/FormTitle';

import { ActionButton } from '@src/components/ActionButton';
import { FormButtons } from '@src/components/Form/FormButtons';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { style, classes } from 'typestyle';
import { WidgetView } from 'common';
import { Loader } from '@src/components/Loader';
import { CloseButton } from '@src/components/CloseButton';
import { ADALONG_COLORS } from '@src/styles';
import { Error } from './Error';
import { useMutationCreateWidget } from '../../queries/widgets/useMutationCreateWidget';
import { ModalCloseButtonPosition } from '../../components/Modals/CloseButtonPosition';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';

interface Props {
  onClose(): void
  onCreated?: (widget: WidgetView) => void
}

export function WidgetCreate(props: Props) {
  const { data: userConfig } = useQueryUserConfig();
  const user = userConfig?.user ?? null;
  const productFeature = !!(user?.groupFeatures.product_catalog);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sourceType, setSourceType] = useState<'collection' | 'product'>('collection');

  const {
    mutate: createWidget,
    error,
    isPending: submiting,
  } = useMutationCreateWidget();

  const submit = async () => {
    createWidget({
      name,
      description,
      sourceType,
    }, {
      onSuccess: (widget) => {
        props.onCreated ? props.onCreated(widget) : props.onClose();
      },
    });
  };

  return (
    <OverlayContainer
      style={{ minWidth: 450, padding: 35 }}
      onClose={props.onClose}
    >
      <ModalCloseButtonPosition>
        <CloseButton onClick={props.onClose} />
      </ModalCloseButtonPosition>
      <FormTitle value={lang.title} />
      {productFeature && (
        <div className={typeToggleClass}>
          <button
            type="button"
            onClick={() => setSourceType('collection')}
            className={classes(typeOptionClass, sourceType === 'collection' && typeOptionActiveClass)}
          >
            {lang.typeCollection}
          </button>
          <button
            type="button"
            onClick={() => setSourceType('product')}
            className={classes(typeOptionClass, sourceType === 'product' && typeOptionActiveClass)}
          >
            {lang.typeProduct}
          </button>
        </div>
      )}
      <Input
          onChange={({ target }) => setName(target.value)}
          placeholder={lang.name}
        />
      <TextArea
          placeholder={lang.description}
          onChange={({ target }) => setDescription(target.value)}
          maxLength={1000}
          style={{ margin: '10px 0' }}
        />
      <Error error={error} />
      {submiting && (
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <Loader size={23} />
        </div>
      )}
      <FormButtons className={buttonsClass}>
        <ActionButton
          variant="transparent"
          onAction={props.onClose}
        >
          {lang.close.toUpperCase()}
        </ActionButton>
        <ActionButton
          variant="blue"
          disabled={submiting}
          onAction={submit}
        >
          {lang.submit.toUpperCase()}
        </ActionButton>
      </FormButtons>
    </OverlayContainer>
  );
}

const lang = LocalizedStrings({
  en: {
    title: 'Create a widget',
    typeCollection: 'Collection widget',
    typeProduct: 'Product widget',
    name: 'Widget Name',
    description: 'Description',
    type: 'Source type',
    close: 'Cancel',
    submit: 'Create',
    errors: {
      unknown: 'An error occured',
    },
  },
});

const buttonsClass = style({
  $nest: {
    '& > button': {
      flex: 1,
    },
    '& > button:first-of-type': {
      marginRight: 50,
    },
  },
});

const typeToggleClass = style({
  display: 'flex',
  gap: 8,
  marginBottom: 20,
});

const typeOptionClass = style({
  flex: 1,
  padding: '10px 16px',
  borderRadius: 8,
  border: '2px solid rgba(255,255,255,0.12)',
  background: 'transparent',
  color: ADALONG_COLORS.LIGHT_GRAY,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  $nest: {
    '&:hover': {
      borderColor: 'rgba(255,255,255,0.3)',
      color: ADALONG_COLORS.SMOOTH_WHITE,
    },
  },
});

const typeOptionActiveClass = style({
  borderColor: ADALONG_COLORS.FLASH_BLUE,
  color: ADALONG_COLORS.SMOOTH_WHITE,
});
